import React from "react";
import * as Icons from "lucide-react";
import { Leaf, Sprout, Salad, Heart } from "lucide-react";
import { SectionHeading, PillLink } from "../components/common";
import { GuideMascot } from "../components/GuideMascot";
import { MEALS, GUIDES, PHOTOS } from "../data/content";

const Meals = () => {
  return (
    <div data-testid="meals-page">
      <section className="bg-[#eef4ef] pt-14 pb-16">
        <div className="mx-auto max-w-5xl px-5 md:px-8 flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1">
            <span className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 font-body font-600 text-sm text-[#5f8468] shadow-soft-sm">
              🌿 Organic · Meat-Free Kitchen
            </span>
            <h1 className="mt-4 font-head font-700 tracking-tight text-4xl sm:text-5xl text-[#1E293B]">Meals & Nutrition</h1>
            <p className="mt-4 font-body text-lg text-[#475569] leading-relaxed max-w-2xl">
              We are proudly organic — our food is sourced from the very best, and our kitchen is fully meat-free. Breakfast, lunch, snacks, and dinner are all served to children, fresh, home-cooked, and made with love to nourish growing bodies.
            </p>
          </div>
          <GuideMascot src={GUIDES.leaf} size={150} ring="#84A98C" className="animate-floaty" />
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <SectionHeading center eyebrow="Served daily" title="Four nourishing meals" sub="Wholesome, plant-powered food is part of how we honor the body — one of our five pillars." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {MEALS.map((m, i) => {
              const Icon = Icons[m.icon] || Icons.Utensils;
              const tints = ["#fdf1e6", "#eef4ef", "#eaf2fc", "#f4edff"];
              return (
                <div key={m.name} className="rounded-3xl p-7 text-center shadow-soft-sm hover:-translate-y-1.5 transition-transform duration-200" style={{ background: tints[i] }} data-testid={`meal-card-${m.name.toLowerCase()}`}>
                  <span className="w-16 h-16 mx-auto rounded-2xl bg-white grid place-items-center text-[#5f8468] shadow-soft-sm"><Icon size={28} /></span>
                  <h3 className="mt-4 font-head font-600 text-xl text-[#1E293B]">{m.name}</h3>
                  <p className="mt-2 font-body text-sm text-[#475569] leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-8 bg-white">
        <div className="mx-auto max-w-6xl px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={PHOTOS.meals} alt="Children sharing a wholesome meal" className="w-full aspect-[4/3] object-cover blob-mask shadow-soft" />
            <div className="absolute -bottom-6 -right-4 animate-floaty"><GuideMascot src={GUIDES.wave} size={104} ring="#FFD166" /></div>
          </div>
          <div>
            <SectionHeading eyebrow="From garden to table" title="Food as a way of caring" />
            <div className="mt-6 space-y-4">
              {[
                { icon: Sprout, t: "Grown & gathered together", d: "Gardening and cooking are part of learning. Children get hands-on with real food, from soil to plate." },
                { icon: Salad, t: "Fresh, organic & plant-based", d: "Every meal is meat-free and made from fresh, organic ingredients — no shortcuts." },
                { icon: Heart, t: "Home-cooked with love", d: "Warm, comforting meals shared in community, teaching gratitude and healthy habits for life." },
              ].map((row) => (
                <div key={row.t} className="flex items-start gap-4 bg-[#FDFBF7] rounded-2xl p-5 shadow-soft-sm" data-testid="food-value">
                  <span className="w-11 h-11 rounded-full bg-[#eef4ef] grid place-items-center text-[#5f8468] shrink-0"><row.icon size={20} /></span>
                  <div>
                    <h4 className="font-head font-600 text-[#1E293B]">{row.t}</h4>
                    <p className="mt-1 font-body text-sm text-[#475569] leading-relaxed">{row.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8"><PillLink to="/enroll" variant="sage" testid="meals-enroll-btn">Enroll Your Child</PillLink></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Meals;
